import { useRef, type ReactNode } from "react";
import { useFocusTrap } from "../ui/useFocusTrap";
import { clsx } from "clsx";

export type PanelPlace = "column" | "drawer" | "sheet";

/**
 * Where the task panel sits (docs/plans/flexui.md): beside the page on wide screens (`column`), sliding in over
 * the page on medium ones (`drawer`), filling the screen on narrow ones (`sheet`). The panel stays the same element
 * whichever it is, so rotating a phone or resizing a window keeps what was typed (§9.19). Over the page it keeps
 * keyboard focus inside, closes with Escape or a click beside it, and gives focus back to what opened it.
 */
export function PanelFrame({
  place,
  onClose,
  children,
}: {
  place: PanelPlace;
  onClose(): void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const over = place !== "column";
  useFocusTrap(box, onClose, over);

  return (
    <>
      {over && (
        <div
          className="fixed inset-0 z-30 bg-[rgba(28,27,26,0.32)]"
          aria-hidden
          onClick={onClose}
          data-testid="panel-scrim"
        />
      )}
      <div
        ref={box}
        role={over ? "dialog" : undefined}
        aria-modal={over || undefined}
        className={clsx(
          "flex min-h-0",
          place === "column" && "w-full max-w-md",
          place === "drawer" && "fixed inset-y-0 right-0 z-40 w-[min(28rem,100%)] shadow-[var(--shadow-lg)]",
          place === "sheet" &&
            "fixed inset-0 z-40 bg-[var(--surface-2)] pt-[env(safe-area-inset-top)] *:max-w-none",
        )}
        data-testid="panel-frame"
        data-place={place}
      >
        {children}
      </div>
    </>
  );
}
